import React from 'react';
import { pricingPlans } from '../data/mockData';
import { Button } from './ui/button';
import { Check, Sparkles } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PricingCard = ({ plan, onOpenContactForm, delay }) => {
  const card = useScrollAnimation({ threshold: 0.15 });

  return (
    <div
      ref={card.ref}
      data-testid={`pricing-card-${plan.id}`}
      className={`group relative flex flex-col rounded-2xl transition-all duration-500 scroll-fade-up scroll-delay-${delay} ${card.isVisible ? 'scroll-visible' : ''} ${
        plan.recommended
          ? 'bg-gradient-to-br from-[#2d1052] to-[#4C1D95] text-white shadow-2xl shadow-purple-500/20 scale-[1.03] z-10 border-2 border-[#7C3AED]/30'
          : 'bg-white text-zinc-900 border border-zinc-200 hover:border-[#7C3AED]/30 hover:shadow-[0_20px_50px_rgba(124,58,237,0.15)] hover:-translate-y-2'
      } p-8`}
    >
      {plan.recommended && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-[#14B8A6] to-[#5DE4D4] text-white text-xs font-bold tracking-wider px-5 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-teal-500/25">
            <Sparkles className="w-3 h-3" /> BEST VALUE
          </span>
        </div>
      )}

      <div className="mb-6 mt-2">
        <h3 className={`text-lg font-bold mb-3 ${plan.recommended ? 'text-white/80' : 'text-zinc-500'}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {plan.name}
        </h3>
        <div className="flex items-baseline gap-1">
          <span className={`text-5xl font-black tracking-tight ${plan.recommended ? 'text-white' : 'text-zinc-900'}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {plan.price}
          </span>
        </div>
        <p className={`text-xs mt-1.5 ${plan.recommended ? 'text-white/40' : 'text-zinc-400'}`}>
          {plan.federal}
        </p>
      </div>

      <ul className="space-y-3 flex-1 mb-8">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.recommended ? 'bg-[#14B8A6]/20' : 'bg-[#7C3AED]/10'}`}>
              <Check className={`w-3 h-3 ${plan.recommended ? 'text-[#14B8A6]' : 'text-[#7C3AED]'}`} />
            </div>
            <span className={`text-sm ${plan.recommended ? 'text-white/70' : 'text-zinc-600'}`}>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <p className={`text-[10px] text-center mb-3 ${plan.recommended ? 'text-white/30' : 'text-zinc-400'}`}>
          {plan.note}
        </p>
        <Button
          data-testid={`pricing-cta-${plan.id}`}
          onClick={onOpenContactForm}
          className={`w-full font-semibold h-11 rounded-full active:scale-[0.97] ${
            plan.recommended
              ? 'bg-[#14B8A6] hover:bg-[#0D9488] text-white shadow-lg shadow-teal-500/25'
              : 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#6D28D9] hover:to-[#5B21B6] text-white shadow-lg shadow-purple-500/20'
          }`}
        >
          {plan.buttonText}
        </Button>
      </div>
    </div>
  );
};

const PricingSection = ({ onOpenContactForm }) => {
  const header = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#7C3AED]/5 blur-[120px]" />

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#14B8A6]/10 text-[#14B8A6] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
            PRICING
          </span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Transparent, Flat-Rate Pricing
          </h2>
          <p className="text-base text-zinc-500 leading-relaxed">
            Choose the plan that fits your needs. Every package includes expert trademark attorney support.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto items-stretch">
          {pricingPlans.map((plan, i) => (
            <PricingCard key={plan.id} plan={plan} onOpenContactForm={onOpenContactForm} delay={i + 1} />
          ))}
        </div>

        <p className="text-center text-sm text-zinc-400 mt-10">
          All packages exclude USPTO filing fees ($250-$350 per class), paid directly to the government.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
